/**
 * The per-game serve switch on the Layer Stack page (LAYERSTACK_READ=
 * per_game): whether Match Video, Insight Labs and /api/v1/serve read
 * this game from the layer stack. Platform QA flips it; the server
 * records who and when (priori_stack_v1.serve_game).
 *
 * The server also says whether the game is stack-red (a build step whose
 * latest run failed) and whether customers are reading it while red; the
 * switch warns on both and never refuses — staff may serve deliberately.
 */
import { useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'
import { useActionLog } from './actionLog'

type ServeState = {
  mode: 'off' | 'per_game' | 'all'
  enabled: boolean
  /** Steps whose latest run failed (stack-red when non-empty); null when
   *  the server could not read the build runs. */
  failed_steps?: string[] | null
  /** Customers read this game from the stack while it is red. */
  red_and_served?: boolean
}

/** The red-and-served warning, or the caution before serving a red game. */
function RedNotice({ state }: { state: ServeState }) {
  const failed = state.failed_steps
  if (failed === null) {
    return (
      <p className="ls-serve-notice" role="status">
        Build status could not be read, so this match may be red. Check the
        Builds tab before serving it.
      </p>
    )
  }
  if (!failed?.length) return null
  const steps = failed.join(', ')
  if (state.red_and_served) {
    return (
      <p className="ls-serve-notice is-served" role="alert"
         data-testid="red-and-served">
        Customers are being served this match while it is red. Failed build
        steps: {steps}. Its panels may be broken or missing until they are
        rebuilt.
      </p>
    )
  }
  if (state.mode !== 'per_game') return null
  return (
    <p className="ls-serve-notice" role="status">
      This match is red (failed build steps: {steps}). Serving it shows
      customers broken or missing panels.
    </p>
  )
}

export function ServeToggle({ gameId }: { gameId: number }) {
  const { log } = useActionLog()
  const [state, setState] = useState<ServeState | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let live = true
    api<ServeState>(`/api/v1/opsqa/games/${gameId}/serve`)
      .then((d) => { if (live) setState(d) })
      .catch(() => { if (live) setState(null) })
    return () => { live = false }
  }, [gameId])

  if (!state) return null
  if (state.mode !== 'per_game') {
    return (
      <>
        <p className="muted" style={{ fontSize: '11px' }}>
          {state.mode === 'all'
            ? 'Every game is served from the layer stack in this environment.'
            : 'Serving from the layer stack is off in this environment.'}
        </p>
        <RedNotice state={state} />
      </>
    )
  }
  const flip = async (enabled: boolean) => {
    // A caution, not a block: serving a red match is a deliberate act.
    if (enabled && state.failed_steps?.length && !window.confirm(
      `This match is red (failed build steps: ${state.failed_steps.join(', ')}). `
      + 'Serving it shows customers broken or missing panels. Serve it anyway?')) {
      return
    }
    setSaving(true)
    try {
      const d = await api<ServeState>(`/api/v1/opsqa/games/${gameId}/serve`,
        { method: 'POST', body: JSON.stringify({ enabled }) })
      setState(d)
      log('done', enabled
        ? 'Serving this game from the layer stack (Match Video, Insight Labs)'
        : "Serving this game from the pre 'layers' analytics again")
    } catch (e: unknown) {
      log('fail', `Serve switch failed — ${e instanceof ApiError ? e.message : String(e)}`)
    } finally {
      setSaving(false)
    }
  }
  return (
    <>
      <label className="ls-flag"
             title="Match Video panels, Insight Labs and /api/v1/serve read this game from the layer stack (R2). Off: the pre 'layers' analytics.">
        <input type="checkbox" checked={state.enabled} disabled={saving}
               onChange={(e) => void flip(e.target.checked)} />
        Serve this game from the layer stack
      </label>
      <RedNotice state={state} />
    </>
  )
}
